/**
 * Shared persistence contract for the Plan Drawing feature.
 *
 * IMPORTANT: This file MUST stay byte-for-byte compatible (in behaviour) with the
 * mobile counterpart at `mobile/src/app/pages/plan-draw/plan-draw.contract.ts`.
 * A plan drawn on one platform has to be re-editable on the other, so the
 * `-draw.json` envelope schema, the logical coordinate base and the upload
 * naming convention are defined here in one place and mirrored verbatim.
 */

/** Bump when the envelope shape changes in a non-backward-compatible way. */
export const PLAN_DRAW_SCHEMA_VERSION = 1;

/** Suffix of the re-editable drawing-layer upload. */
export const DRAW_JSON_SUFFIX = '-draw.json';
/** Suffix of the original (un-annotated) background photo upload. */
export const BACKGROUND_SUFFIX = '-bg';
/** Suffix of the rendered interactive-plan composite (background + strokes + markers). */
export const COMPOSITE_SUFFIX = '-iplan';

/**
 * Envelope written as the `-draw.json` upload.
 * Only pen strokes + shapes live here - never indicator markers (those are
 * re-loaded fresh from `room_indikator.position_x/y`).
 */
export interface PlanDrawEnvelope {
  /** Schema version, see PLAN_DRAW_SCHEMA_VERSION. */
  version: number;
  /** Logical coordinate base (original background pixels, or the blank canvas size). */
  logicalWidth: number;
  logicalHeight: number;
  /** Parsed result of `drawLayer.toJSON()` (Konva node tree). */
  konva: any;
}

/** Replace characters that are unsafe / inconsistent across upload backends. */
function sanitizeBase(bezeichnung: string): string {
  return (bezeichnung || 'plan')
    .trim()
    .replace(/\.[a-z0-9]+$/i, '') // drop a trailing extension if one slipped in
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_') || 'plan';
}

/** `{bezeichnung}-draw.json` */
export function drawJsonFilename(bezeichnung: string): string {
  return `${sanitizeBase(bezeichnung)}${DRAW_JSON_SUFFIX}`;
}

/** `{bezeichnung}-bg.{ext}` */
export function backgroundFilename(bezeichnung: string, ext: string = 'jpg'): string {
  return `${sanitizeBase(bezeichnung)}${BACKGROUND_SUFFIX}.${(ext || 'jpg').replace(/^\./, '')}`;
}

/** `{bezeichnung}.{ext}` - the composite/base image stored on the plan. */
export function compositeFilename(bezeichnung: string, ext: string = 'png'): string {
  return `${sanitizeBase(bezeichnung)}.${(ext || 'png').replace(/^\./, '')}`;
}

/** True when an upload's original filename is the re-editable drawing JSON. */
export function isDrawJsonFile(originalFilename?: string | null): boolean {
  return !!originalFilename && originalFilename.toLowerCase().endsWith(DRAW_JSON_SUFFIX);
}

/** True when an upload's original filename is the stored original background. */
export function isBackgroundFile(originalFilename?: string | null): boolean {
  if (!originalFilename) {
    return false;
  }
  const lower = originalFilename.toLowerCase();
  const dot = lower.lastIndexOf('.');
  const base = dot === -1 ? lower : lower.substring(0, dot);
  return base.endsWith(BACKGROUND_SUFFIX);
}

/** Build the envelope object that is persisted as `-draw.json`. */
export function buildDrawEnvelope(layerJson: string, logicalWidth: number, logicalHeight: number): PlanDrawEnvelope {
  let konva: any;
  try {
    konva = JSON.parse(layerJson);
  } catch {
    konva = layerJson;
  }
  return {
    version: PLAN_DRAW_SCHEMA_VERSION,
    logicalWidth: Math.round(logicalWidth),
    logicalHeight: Math.round(logicalHeight),
    konva
  };
}

/** Safely parse a `-draw.json` payload (string or already-parsed object). */
export function parseDrawEnvelope(raw: string | object): PlanDrawEnvelope | null {
  try {
    const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (obj && typeof obj === 'object' && 'konva' in obj) {
      return obj as PlanDrawEnvelope;
    }
    return null;
  } catch {
    return null;
  }
}
